/**
 * TaskPhases - Página de gerenciamento de fases de tarefas
 * 
 * Características:
 * - Filtro por categoria
 * - Listagem em tabela com drag visual (sort_order)
 * - CRUD completo via Dialog
 * - Seleção de cor e flag "fase final"
 * 
 * @author Sistema
 * @version 1.2.0
 * RELEASE: 2026-08-22 v1.2.0 - Título dinâmico por módulo (Tarefas | Retenção | Jurídico)
 * RELEASE: 2026-04-06 v1.0.0 - Criação da página
 * RELEASE: 2026-04-19 v1.1.0 - Alinhamento ao padrão de header do módulo de Contatos
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Pencil,
  Trash2,
  Columns,
  Loader2,
  Search,
  MoreHorizontal,
  Flag,
  CheckCircle,
  ClipboardList,
} from 'lucide-react';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTaskPhases, TaskPhase, UpsertTaskPhaseInput } from '@/hooks/useTaskPhases';
import { useTaskCategories } from '@/hooks/useTaskCategories';
import { useTaskModule } from '@/hooks/useTaskModuleType';
import { HexColorPicker } from '@/components/tasks/HexColorPicker';

// Cores disponíveis para seleção
const AVAILABLE_COLORS = [
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#f43f5e', '#ef4444', '#f97316',
  '#eab308', '#22c55e', '#14b8a6', '#06b6d4',
  '#0ea5e9', '#64748b', '#78716c', '#475569',
];

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function TaskPhases() {
  const { t } = useTranslation('common');
  // v1.2.0: módulo corrente (Tarefas | Retenção | Jurídico) para rótulos dinâmicos
  const taskModule = useTaskModule();
  const { categories, isLoading: isCategoriesLoading } = useTaskCategories();
  
  // Filtro de categoria selecionada
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const {
    phases,
    isLoading,
    upsert,
    isUpserting,
    remove,
    isRemoving,
  } = useTaskPhases(selectedCategoryId || undefined);

  // Estado do Dialog de criar/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<TaskPhase | null>(null);
  const [search, setSearch] = useState('');

  // Estado do formulário
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formColor, setFormColor] = useState(AVAILABLE_COLORS[0]);
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsFinal, setFormIsFinal] = useState(false);
  // Marcador "Padrão" da fase (opcional)
  const [formIsDefault, setFormIsDefault] = useState(false);

  // Dialog de confirmação
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  // Fases filtradas por busca
  const filtered = phases.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.description?.toLowerCase().includes(search.toLowerCase())
  );

  /**
   * Abre o dialog para criar nova fase
   */
  const handleCreate = () => {
    setEditingPhase(null);
    setFormCategoryId(selectedCategoryId || (categories[0]?.id || ''));
    setFormName('');
    setFormDescription('');
    setFormColor(AVAILABLE_COLORS[0]);
    setFormSortOrder(phases.length + 1);
    setFormIsFinal(false);
    setFormIsDefault(false);
    setDialogOpen(true);
  };

  /**
   * Abre o dialog para editar fase existente
   */
  const handleEdit = (phase: TaskPhase) => {
    setEditingPhase(phase);
    setFormCategoryId(phase.category_id);
    setFormName(phase.name);
    setFormDescription(phase.description || '');
    setFormColor(phase.color || AVAILABLE_COLORS[0]);
    setFormSortOrder(phase.sort_order);
    setFormIsFinal(phase.is_final);
    setFormIsDefault(!!phase.is_default);
    setDialogOpen(true);
  };

  /**
   * Salva a fase (criar ou atualizar)
   */
  const handleSave = async () => {
    if (!formName.trim() || !formCategoryId) return;

    const input: UpsertTaskPhaseInput = {
      p_category_id: formCategoryId,
      p_name: formName.trim(),
      p_description: formDescription.trim() || undefined,
      p_color: formColor,
      p_sort_order: formSortOrder,
      p_is_final: formIsFinal,
      p_is_default: formIsDefault,
    };

    if (editingPhase) {
      input.p_id = editingPhase.id;
    }

    await upsert(input);
    setDialogOpen(false);
  };

  /**
   * Exclui uma fase com confirmação
   */
  const handleDelete = async (phase: TaskPhase) => {
    const confirmed = await confirmDialog({
      title: 'Excluir Fase',
      description: `Deseja realmente excluir a fase "${phase.name}"? Esta ação não pode ser desfeita.`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (confirmed) {
      await remove(phase.id);
    }
  };

  // Estado de carregamento
  if (isLoading && isCategoriesLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto (padrão Contatos) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Fases de {taskModule.label}</h1>
          <p className="text-xs text-muted-foreground">
            Configure as fases do Kanban para cada categoria
          </p>
        </div>
        <Button
          size="xs"
          onClick={handleCreate}
          disabled={categories.length === 0}
          className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
        >
          <Plus className="mr-1.5" />
          Nova Fase
        </Button>
      </div>

      <div className="px-4 py-4 space-y-4">

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={selectedCategoryId || 'all'} onValueChange={(v) => setSelectedCategoryId(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-64">
            <SelectValue placeholder="Todas as categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar fases..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Tabela de Fases */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Columns className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-lg font-medium">Nenhuma fase encontrada</p>
            <p className="text-sm mt-1">
              {categories.length === 0
                ? 'Crie uma categoria primeiro para poder adicionar fases'
                : search
                  ? 'Tente ajustar sua busca'
                  : 'Crie a primeira fase para montar o Kanban'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Cor</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="hidden md:table-cell">Categoria</TableHead>
                <TableHead className="hidden sm:table-cell text-center">Ordem</TableHead>
                <TableHead className="text-center">Tarefas</TableHead>
                <TableHead className="text-center">Final</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((phase) => (
                <TableRow key={phase.id}>
                  <TableCell>
                    <div
                      className="h-6 w-6 rounded-md"
                      style={{ backgroundColor: phase.color || '#64748b' }}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{phase.name}</p>
                      {phase.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{phase.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="outline" className="text-xs">
                      <div
                        className="h-2 w-2 rounded-full mr-1.5"
                        style={{ backgroundColor: phase.category_color }}
                      />
                      {phase.category_name}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-center">
                    <span className="text-sm text-muted-foreground">{phase.sort_order}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" className="text-xs">
                      {phase.tasks_count}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      {phase.is_final && (
                        <Flag className="h-4 w-4 text-emerald-500" />
                      )}
                      {/* Marcador "Padrão" (opcional) */}
                      {phase.is_default && (
                        <Badge variant="outline" className="text-[10px]">Padrão</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(phase)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(phase)}
                          className="text-destructive focus:text-destructive"
                          disabled={phase.tasks_count > 0}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
          {/* Cabeçalho padrão premium (mesmo do modal "Nova anotação") */}
          <PremiumDialogHeader
            icon={Columns}
            title={editingPhase ? 'Editar Fase' : 'Nova Fase'}
            description={
              editingPhase
                ? 'Atualize os dados da fase'
                : 'Preencha os dados para criar uma nova fase do Kanban'
            }
          />

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {/* Categoria */}
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select value={formCategoryId} onValueChange={setFormCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="phase-name">Nome *</Label>
              <Input
                id="phase-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex: A Fazer, Em Andamento, Concluído..."
                maxLength={100}
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="phase-description">Descrição</Label>
              <Textarea
                id="phase-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Descrição opcional da fase"
                rows={2}
                maxLength={500}
              />
            </div>

            {/* Cor e Ordem */}
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <HexColorPicker value={formColor} onChange={setFormColor} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phase-sort-order">Ordem</Label>
                <Input
                  id="phase-sort-order"
                  type="number"
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(parseInt(e.target.value) || 1)}
                  min={1}
                />
              </div>
            </div>

            {/* Flag Fase Final */}
            <div className="flex items-center gap-2">
              <Switch
                id="is-final"
                checked={formIsFinal}
                onCheckedChange={setFormIsFinal}
              />
              <Label htmlFor="is-final" className="text-sm">Fase final</Label>
            </div>

            {/* Flag Fase Padrão (opcional) — usada para pré-selecionar e travar
                a fase na criação de solicitações de Retenção/Jurídico */}
            <div className="flex items-start gap-2">
              <Switch
                id="is-default-phase"
                checked={formIsDefault}
                onCheckedChange={setFormIsDefault}
              />
              <div className="space-y-0.5">
                <Label htmlFor="is-default-phase" className="text-sm">Fase padrão</Label>
                <p className="text-xs text-muted-foreground">
                  Opcional. Quando marcada, novas solicitações já nascem nesta fase.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-muted/30 px-6 py-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formName.trim() || !formCategoryId || isUpserting}
            >
              {isUpserting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingPhase ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação */}
      {ConfirmDialogComponent}
      </div>
    </div>
  );
}
